/**
 * Authentication Store
 * Manages user authentication state with Firebase and localStorage
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types'
import { startTokenRefresh, stopTokenRefresh } from '@/utils/tokenRefresh'
import { onAuthStateChange } from '@/config/firebase'
import type { User as FirebaseUser } from 'firebase/auth'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const firebaseUser = ref<FirebaseUser | null>(null)
  const isInitialized = ref(false)

  // Computed
  const isAuthenticated = computed(() => user.value !== null && firebaseUser.value !== null)

  // Initialize token from localStorage (for immediate auth checks before Firebase loads)
  const storedToken = localStorage.getItem('auth_token')
  if (storedToken) {
    token.value = storedToken
  }

  // Actions

  /**
   * Set current user
   */
  function setUser(newUser: User | null) {
    user.value = newUser
  }

  /**
   * Set authentication token
   * Stores token in localStorage for persistence
   * Starts automatic token refresh when token is set
   */
  function setToken(newToken: string | null) {
    token.value = newToken

    if (newToken) {
      localStorage.setItem('auth_token', newToken)
      // Start automatic token refresh
      startTokenRefresh()
    } else {
      localStorage.removeItem('auth_token')
      // Stop token refresh when token is cleared
      stopTokenRefresh()
    }
  }

  /**
   * Set Firebase user
   * This is updated by the auth state listener
   * Creates a new object reference to trigger Vue reactivity when properties change
   */
  function setFirebaseUser(newFirebaseUser: FirebaseUser | null) {
    if (newFirebaseUser) {
      // Create a shallow copy to trigger reactivity when displayName or other properties change
      firebaseUser.value = { ...newFirebaseUser }
    } else {
      firebaseUser.value = null
    }
  }

  /**
   * Clear authentication state
   * Called on logout
   */
  function clearAuth() {
    user.value = null
    token.value = null
    firebaseUser.value = null
    localStorage.removeItem('auth_token')
    // Stop token refresh on logout
    stopTokenRefresh()
  }

  /**
   * Initialize auth state listener
   * Sets up Firebase auth state change observer
   * Should be called once when app starts
   */
  function initializeAuthListener() {
    if (isInitialized.value) {
      return // Already initialized
    }

    onAuthStateChange(async (fbUser) => {
      setFirebaseUser(fbUser)

      if (fbUser) {
        // User signed in - get fresh token
        try {
          const newToken = await fbUser.getIdToken()
          setToken(newToken)
        } catch (error) {
          console.error('Failed to get token from Firebase user:', error)
          // Token retrieval failed, clear auth
          clearAuth()
        }
      } else {
        // User signed out or not yet initialized
        // IMPORTANT: Only clear auth if we were previously authenticated
        // This prevents clearing on initial page load when Firebase hasn't restored session yet
        if (isInitialized.value && token.value !== null) {
          // We were authenticated but now we're not - user logged out
          clearAuth()
        }
      }

      // Mark as initialized after first auth state check
      // This allows the auth guard to proceed
      isInitialized.value = true
    })
  }

  return {
    // State
    user,
    token,
    firebaseUser,
    isInitialized,
    // Computed
    isAuthenticated,
    // Actions
    setUser,
    setToken,
    setFirebaseUser,
    clearAuth,
    initializeAuthListener,
  }
})